import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../api";
import RailGroup from "./RailGroup";
import { listsOn, noContactOf, wouldReturnTo, rolesTrail, shortDate, actorWord, personName } from "../lists/listsModel.mjs";
import { Icon, addToList, useConflictAsk } from "../lists/parts";
import "../lists/lists.css";

/*
 * LISTS ON THE LEAD RECORD (Lists canvas, frame 6). The lists this PERSON is on
 * (a list holds people, so a lead's lists are their person's), "+ Add to list"
 * as checkboxes, and No Contact as its own hatched state with where taking them
 * off would put them back. "Roles" names every company we have them at.
 *
 * Its own fetch (GET /contacts/:id/person) rather than a field on the record
 * bundle: the record is the contact, and the person is a different thing.
 * Nothing is drawn for a junk contact, which has no person.
 */
export default function ListsGroup({ record, sheet }) {
  const contactId = record.id || record.contact?.id;
  const [person, setPerson] = useState(undefined);
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const conflictAsk = useConflictAsk("");

  const load = useCallback(async () => {
    try {
      const [{ person: p }, { lists: ls }] = await Promise.all([api(`/contacts/${contactId}/person`), api("/lists")]);
      setPerson(p);
      setLists(ls);
    } catch (e) {
      setPerson(null);
    }
  }, [contactId]);
  useEffect(() => {
    load();
  }, [load]);

  if (!person) return null;

  const on = listsOn(person);
  const nc = noContactOf(person);
  const back = wouldReturnTo(person);
  const first = (personName(person).split(" ")[0]) || "them";

  const run = async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const pid = person.person_id;
  const post = (path, body) => api(path, { method: "POST", body: JSON.stringify(body) });
  const setOn = (list, want) =>
    run(async () => {
      if (want) await addToList(list, [pid], conflictAsk.askFor(list.name));
      else await post(`/lists/${list.id}/members/remove`, { person_ids: [pid] });
    });

  const summary = nc ? (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Icon.ban size={12} />No Contact</span>
  ) : on.length ? (
    on.map((l) => l.name).join(" · ")
  ) : (
    "On no lists"
  );

  return (
    <RailGroup id="lists" title="Lists" count={nc ? null : on.length || null} summary={summary} open={open} onToggle={setOpen} sheet={sheet}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {err && <p className="crm-login__err">{err}</p>}
        {nc ? (
          <>
            <div className="lst-nc-box lst-hatch">
              <span className="lst-onwhite" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Icon.ban size={13} /><b>No Contact</b></span>
              <span className="lst-onwhite lst-note">since {shortDate(nc.added_at)} {actorWord(nc.changed_by)}</span>
              <span className="lst-onwhite">
                <button type="button" className="lst-link" style={{ color: "var(--crm-black)" }} disabled={busy}
                  onClick={() => run(() => post("/lists/no-contact/members/remove", { person_ids: [pid] }))}>
                  Take off No Contact
                </button>
              </span>
            </div>
            {back.length > 0 && (
              <div className="lst-note" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                Goes back to: {back.map((l) => <span key={l.id} className="lst-ghost">{l.name}</span>)}
              </div>
            )}
          </>
        ) : (
          <div role="group" aria-label={`Lists ${first} is on`} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {lists.filter((l) => l.kind === "standard").map((l) => {
              const mine = on.find((o) => o.id === l.id);
              // A rule list's membership is the rule's; it is changed from the
              // list or the person, where the reason is on screen.
              const byRule = mine && mine.source === "rule";
              return (
                <label key={l.id} className="lst-opt lst-opt--check">
                  <input type="checkbox" checked={!!mine} disabled={busy || byRule} onChange={(e) => setOn(l, e.target.checked)} />
                  {l.name}
                  {byRule && <small>rule</small>}
                </label>
              );
            })}
            <div className="lst-pop__div" />
            <label className="lst-opt lst-opt--check">
              <input type="checkbox" checked={false} disabled={busy}
                onChange={() => run(() => post("/lists/no-contact/members", { person_ids: [pid] }))} />
              No Contact
              <small>takes {first} off all</small>
            </label>
          </div>
        )}
        {person.role_count > 1 && (
          <div className="lst-note"><b style={{ color: "var(--crm-black)" }}>Roles</b> {rolesTrail(person)}</div>
        )}
      </div>
      {conflictAsk.el}
    </RailGroup>
  );
}
